import React, { useState, useEffect, useRef, Fragment, createRef, useCallback, useMemo } from "react";
import { Modal, Layout, Select, Col, Row, Typography } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import _ from "lodash";
import "./FilePreviewer.scss";
import Button_v2 from "./Button_v2";
import {
  DownloadIcon,
  PrintIcon,
  SendAsEmailIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "../../common/CustomIcon";
import {  PdfPreviewPictures } from "../../api/OrdersApi";
import { renderAlertMessage } from "../../helpers/common";
import { alertMessage } from "../../common/Common";
import CustomDropdown from "./CustomDropdown";


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const { Sider, Content } = Layout;

const FilePreviewer = React.memo(({
  file,
  previewVisible,
  setPreviewVisible,
  previewFileUrl,
  setPreviewFileUrl,
  previewFileType,
  setPreviewFileType,
  setIsHovered,
  predefinedDocsCode,
  requestedAs = "",
  toggleSelectDocument = () => {},
  onPrint = () => {},
  setIsPODemailModalVisible,
  currentOrder,
  selectedFile,
  refreshDetails,
  show,
  locObj,
  setSelectedDocs = () => {},
}) => {
  const [numPages, setNumPages] = useState(0);
  const selectedPageRef = useRef(selectedPage);
  // const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPage, setSelectedPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [pageSelections, setPageSelections] = useState({});
  const [uploadProgress, setUploadProgress] = useState(false);
  const [locationId, setLocationId] = useState(null)
  // Refs for the main PDF container, page elements, sidebar container, and thumbnail elements.
  const containerRef = useRef(null);
  const pageRefs = useRef([]);
  const thumbnailContainerRef = useRef(null);
  const thumbnailRefs = useRef([]);

  // When closing the modal, reset the selected page, page selections, and zoom.
  useEffect(() => {
    setLocationId(locObj?.id)
    if (!previewVisible) {
      setSelectedPage(1);
      setPageSelections({});
      setScale(1);
    }
  }, [previewVisible, locObj,previewFileUrl]);

  // When the PDF loads, initialize the number of pages and set up refs.
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    pageRefs.current = Array.from({ length: numPages }, () => createRef());
    thumbnailRefs.current = Array.from({ length: numPages }, () => createRef());
  };

  // When a thumbnail is clicked, scroll that page into view.
  const scrollToPage = (pageNumber) => {
    const ref = pageRefs.current[pageNumber - 1];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setSelectedPage(pageNumber);
    }
  };

  // Use IntersectionObserver to update the sidebar as pages become visible.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const options = {
      root: container,
      threshold: 0.5,
    };

    const observerCallback = (entries) => {
      let highestRatio = 0;
      let visiblePage = selectedPage;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          highestRatio = entry.intersectionRatio;
          visiblePage = parseInt(entry.target.getAttribute("data-page"), 10);
        }
      });
      if (visiblePage !== selectedPage) {
        setSelectedPage(visiblePage);
      }
    };

    const observer = new IntersectionObserver(observerCallback, options);

    // Attach observer to each page element.
    pageRefs.current.forEach((ref, index) => {
      if (ref.current) {
        ref.current.setAttribute("data-page", index + 1);
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [numPages, selectedPage]);

  
  useEffect(() => {
    // dummyRequest(previewFileUrl)
    const thumbnail = thumbnailRefs.current[selectedPage - 1];
    if (thumbnail && thumbnail.current && thumbnailContainerRef.current) {
      thumbnail.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedPage,previewFileUrl]);

  const prepareUploadPayload = async () => {

    // 1. Fetch the actual PDF file from the blob URL
    const response = await fetch(previewFileUrl);
    const blob = await response.blob();
    const file = new File([blob], 'sample.pdf', { type: 'application/pdf' });
  
    // 2. Format page labels: only lowercase the key if it's 'SIGNATURE'
    const formattedLabels = {};
    Object.entries(pageSelections).forEach(([key, val]) => {
      const formattedKey = key === 'SIGNATURE' ? key.toLowerCase() : key;
      formattedLabels[formattedKey] = val.map(pageNum => String(pageNum));
    });
  
    // 3. Build final payload
    return {
      account_id: currentOrder.account.account_id,
      organization_id: currentOrder.customer_order_details.organization_id,
      order_id: currentOrder.id,
      page_labels: formattedLabels,
      uploaded_file: [file], // Must be an array
      location_id: locationId,
      org_id: currentOrder.customer_order_details.organization_id, // Optional if needed
    };
  };
  

  const handleUploadPdfPictures = async () => {
    setUploadProgress(true);
    const data = await prepareUploadPayload();
    
    PdfPreviewPictures(data)
      .then((result) => {
        if (result.success) {
          alertMessage("Added Successfully", 'success', 5);
          setUploadProgress(false);
          setPreviewVisible(false);
          refreshDetails();
        } else {
          renderAlertMessage(result.errors);
          setUploadProgress(false);
        }
      });
  };
  

//  const handleUploadPdfPictures = () => {
//     const data = {
//       account_id : currentOrder.account.account_id,
//       organization_id : currentOrder.customer_order_details.organization_id,
//       order_id : currentOrder.id,
//       page_labels : selectedPage,
//       uploaded_file : previewFileUrl,
//       location_id : currentOrder.delivery_location_details.location_id,
//     }

//     setUploadProgress(true);
//     PdfPreviewPictures(data)
//     .then((result) => {
//         if (result.success) {
//             alertMessage("Added Successfully", 'success', 5);
//             setUploadProgress(false);
//             setPreviewVisible(false);
//             // ({showOrderPreview: false})
//             // getOrderDetails()

//         } else {
//             renderAlertMessage(result.errors);
//         }
//     });
// };

  const handleCancelPreview = () => {
    setPreviewVisible(false);

    setPreviewFileUrl(null);
    setPreviewFileType(null);
    setIsHovered(false);

      // 🧹 Clear the flag so modal can open again next time
  localStorage.removeItem("filePreviewModalOpened");
  };

  // Helper function to get the current label for a page.
  const getPageLabel = (pageNumber) => {
    for (const label in pageSelections) {
      if (pageSelections[label].includes(pageNumber)) {
        return label;
      }
    }
    return "--SELECT--";
  };

  // Update the label for a specific page.
  const handleLabelChange = (pageNumber, value) => {
    setPageSelections((prev) => {
      const newSelections = { ...prev };
      // Remove the page number from all existing label arrays.
      Object.keys(newSelections).forEach((key) => {
        newSelections[key] = newSelections[key].filter(
          (page) => page !== pageNumber
        );
        // Remove the key if the array becomes empty.
        if (newSelections[key].length === 0) {
          delete newSelections[key];
        }
      });
      // If a valid label is selected, add the page number to that label's array.
      if (value !== "--SELECT--") {
        if (newSelections[value]) {
          newSelections[value] = [...newSelections[value], pageNumber];
        } else {
          newSelections[value] = [pageNumber];
        }
      }
      return newSelections;
    });
  };

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 2.5));
  };

  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
  };

  const renderPreviewContent = () => {
    if (!previewFileUrl || !previewFileType) return null;

    if (previewFileType === "image") {
      return (
        <img
          alt="preview"
          className="image-preview"
          style={{
            transform: `scale(${scale})`,
            transition: "transform 0.2s ease-in-out",
            maxWidth: "100%",
            maxHeight: "100%",
          }}
          src={previewFileUrl}
        />
      );
    }

    if (previewFileType === "pdf") {
      return (
        <Fragment>
          <Layout className="pdf-preview-layout">
            {/* Sidebar for page thumbnails and dropdowns */}
            <Sider width={140} className="pdf-preview-sider">
              <div
                ref={thumbnailContainerRef}
                className="pdf-thumbnail-container"
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div
                    key={i}
                    ref={thumbnailRefs.current[i]}
                    className={`pdf-thumbnail ${
                      selectedPage === i + 1 ? "selected" : ""
                    }`}
                    onClick={() => scrollToPage(i + 1)}
                  >
                    <Document file={previewFileUrl}>
                      <Page pageNumber={i + 1} width={180} />
                    </Document>
                    {requestedAs === "mapped_upload" && show == true && (
                        <CustomDropdown
                          pageNumber={i + 1}
                          currentValue={getPageLabel(i + 1)}
                          predefinedDocsCode={predefinedDocsCode}
                          onChange={(value) => handleLabelChange(i + 1, value)}
                        />
                      )}
                  </div>
                ))}
              </div>
            </Sider>

            {/* Scrollable PDF Content */}
            <Content className="pdf-preview-content">
              <div ref={containerRef} className="pdf-scroll-container">
                <Document
                  file={previewFileUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading="Loading PDF..."
                >
                  {Array.from({ length: numPages }, (_, i) => (
                    <div
                      key={`page_${i + 1}`}
                      ref={pageRefs.current[i]}
                      className="pdf-page"
                    >
                      <Page pageNumber={i + 1} width={960 * scale} />
                    </div>
                  ))}
                </Document>
              </div>
            </Content>
          </Layout>
          {/* Display the page labels mapping */}
          {/* <div className="page-labels-mapping" style={{ marginTop: 16 }}>
            {Object.keys(pageSelections).map((label) => (
              <div key={label}>
                page_labels[{label}][]: {pageSelections[label].join(", ")}
              </div>
            ))}
          </div> */}
        </Fragment>
      );
    }

    return (
      <div className="file-preview-error">
        <p className="file-preview-error-text">
          Preview not available for this file type.
        </p>
        <a className="file-preview-error-button" href={previewFileUrl} download>
          Download {previewFileType}
        </a>
      </div>
    );
  };

  const renderActionContainer = () => {
    
    if (requestedAs === "mapped_upload") {
      return (
        <div style={{ display: "flex", justifyContent: show ? "flex-end" : "center" }}>
       {show == false &&  <div
         style={{
           display: "flex",
           justifyContent: "center",
           gap: "8px",
           textAlign: "center",
         }}
       >
         <div
           style={{
             display: "flex",
             justifyContent: "center",
             alignItems: "center",
             width: "360px",
             height: "56px",
             borderRadius: "64px",
             gap: "24px",
             boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.28)",
           }}
         >
           <div
             style={{
               display: "flex",
               gap: "16px",
               padding: "8px",
             }}
           >
             <ZoomOutIcon
               style={{ cursor: "pointer" }}
               onClick={handleZoomOut}
             />
             <ZoomInIcon style={{ cursor: "pointer" }} onClick={handleZoomIn} />
           </div>
           {previewFileType === "pdf" && (
             <div>
               <Typography.Text
                 style={{
                   fontFamily: "Lato",
                   fontWeight: "16px",
                   color: "#0A0C15",
                 }}
               >
                 Page {selectedPage} of {numPages}
               </Typography.Text>
             </div>
           )}
           {file?.id && (
             <div
               style={{
                 display: "flex",
                 gap: "16px",
                 padding: "8px",
               }}
             >
               <DownloadIcon
                 color="#5064AE"
                 style={{ cursor: "pointer" }}
                 onClick={() => onPrint(true)}
               />
               <SendAsEmailIcon
                 style={{ cursor: "pointer" }}
                 onClick={() => {
                   setIsPODemailModalVisible(true);
                 }}
               />
               <PrintIcon
                 color="#5064AE"
                 style={{ cursor: "pointer" }}
                 onClick={() => onPrint()}
               />
             </div>
           )}
         </div>
       </div>}
     { show == true && <div
          style={{
            display: "flex",
            justifyContent: "end",
            gap: "8px",
            textAlign: "center",
          }}
        >
          <Button_v2
            type="flat"
            // loading={isUploading}
            onClick={handleCancelPreview}
            text="Cancel"
          />
        <Button_v2
            key="submit"
            type="primary"
            onClick={handleUploadPdfPictures}
            loading={uploadProgress}
            text="Upload"
          />
        </div>}
       </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "360px",
            height: "56px",
            borderRadius: "64px",
            gap: "24px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.28)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "16px",
              padding: "8px",
            }}
          >
            <ZoomOutIcon
              style={{ cursor: "pointer" }}
              onClick={handleZoomOut}
            />
            <ZoomInIcon style={{ cursor: "pointer" }} onClick={handleZoomIn} />
          </div>
          {previewFileType === "pdf" && (
            <div>
              <Typography.Text
                style={{
                  fontFamily: "Lato",
                  fontWeight: "16px",
                  color: "#0A0C15",
                }}
              >
                Page {selectedPage} of {numPages}
              </Typography.Text>
            </div>
          )}
          {file?.id && (
            <div
              style={{
                display: "flex",
                gap: "16px",
                padding: "8px",
              }}
            >
              <DownloadIcon
                color="#5064AE"
                style={{ cursor: "pointer" }}
                onClick={() => onPrint(true)}
              />
              <SendAsEmailIcon
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setIsPODemailModalVisible(true);
                }}
              />
              <PrintIcon
                color="#5064AE"
                style={{ cursor: "pointer" }}
                onClick={() => onPrint()}
              />
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <Fragment>
   <Modal
   key={`file-preview-modal-${file?.id}`}
  className="file-previewer-modal"
  visible={previewVisible}
  footer={renderActionContainer()}
  closable={!show}
  onCancel={handleCancelPreview}
  style={{
    background:
      previewFileType && previewFileType !== "image"
        ? "rgba(255, 255, 255, 0.1)"
        : "white",
    top: "5vh",
  }}
  bodyStyle={{
    height: "85vh",
    overflow:
      previewFileType && previewFileType !== "image" ? "auto" : "hidden",
    backgroundColor:
      previewFileType && previewFileType !== "image"
        ? "rgba(0, 0, 0, 0.5)"
        : "#fff",
  }}
  width={1200}
>
  {renderPreviewContent()}
</Modal>
    </Fragment>
  );
});

export default React.memo(FilePreviewer);

